// Runtime configuration (design §3.4 step 1, §7.2, erratum E24).
//
// The UI image is environment-portable: nothing environment-specific is baked in at
// build time. At container start, docker-entrypoint.d/10-write-config.sh writes
// /config.json from the Express service env vars, and src/bootstrap.tsx loads it
// before React renders.

export interface RuntimeConfig {
  /** BFF origin, e.g. https://sp-<32 hex>.ecs.us-east-1.on.aws (the BFF Express endpoint; no trailing slash). */
  backendUrl: string;
  cognitoUserPoolId: string;
  cognitoClientId: string;
  /** Cognito hosted-UI domain host, e.g. unishop-dev-<acct>.auth.us-east-1.amazoncognito.com. */
  cognitoDomain: string;
}

export const CONFIG_URL = '/config.json';

const REQUIRED_KEYS: (keyof RuntimeConfig)[] = [
  'backendUrl',
  'cognitoUserPoolId',
  'cognitoClientId',
  'cognitoDomain',
];

let currentConfig: RuntimeConfig | null = null;

export class ConfigError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ConfigError';
  }
}

function normalize(raw: Record<string, unknown>): RuntimeConfig {
  const missing = REQUIRED_KEYS.filter(
    (key) => typeof raw[key] !== 'string' || (raw[key] as string).trim() === '',
  );
  if (missing.length > 0) {
    throw new ConfigError(`${CONFIG_URL} is missing ${missing.join(', ')}`);
  }
  const value = (key: keyof RuntimeConfig) => (raw[key] as string).trim();
  return {
    backendUrl: value('backendUrl').replace(/\/+$/, ''),
    cognitoUserPoolId: value('cognitoUserPoolId'),
    cognitoClientId: value('cognitoClientId'),
    cognitoDomain: value('cognitoDomain').replace(/^https:\/\//, '').replace(/\/+$/, ''),
  };
}

/**
 * Fetches and validates /config.json. Must resolve before React renders; every other
 * module reads the result through getConfig().
 */
export async function loadConfig(fetchImpl: typeof fetch = fetch): Promise<RuntimeConfig> {
  let response: Response;
  try {
    response = await fetchImpl(CONFIG_URL, { cache: 'no-store' });
  } catch {
    throw new ConfigError(`Unable to load ${CONFIG_URL}`);
  }
  if (!response.ok) {
    throw new ConfigError(`Unable to load ${CONFIG_URL} (HTTP ${response.status})`);
  }
  let raw: unknown;
  try {
    raw = await response.json();
  } catch {
    throw new ConfigError(`${CONFIG_URL} is not valid JSON`);
  }
  if (!raw || typeof raw !== 'object') {
    throw new ConfigError(`${CONFIG_URL} is not a JSON object`);
  }
  currentConfig = normalize(raw as Record<string, unknown>);
  return currentConfig;
}

export function getConfig(): RuntimeConfig {
  if (!currentConfig) {
    throw new ConfigError('Runtime config not loaded; call loadConfig() first');
  }
  return currentConfig;
}

/** Test helper: forget the loaded config. */
export function resetConfig(): void {
  currentConfig = null;
}

// ---- Cognito hosted UI (authorization code + PKCE, design §3.4 step 2, ADR 12) ----

export const COGNITO_SCOPES = ['openid', 'email', 'profile'];

export function getRedirectUri(): string {
  return `${window.location.origin}/auth/callback`;
}

/** Must equal the registered ChatLogoutUrl exactly: `https://<ui-host>/` (trailing slash, design §5.1). */
export function getLogoutUri(): string {
  return `${window.location.origin}/`;
}

export function getTokenEndpoint(): string {
  return `https://${getConfig().cognitoDomain}/oauth2/token`;
}

/** Hosted-UI authorize URL for the code flow with an S256 PKCE challenge. */
export function buildAuthorizeUrl(codeChallenge: string, state: string): string {
  const { cognitoDomain, cognitoClientId } = getConfig();
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: cognitoClientId,
    redirect_uri: getRedirectUri(),
    scope: COGNITO_SCOPES.join(' '),
    state,
    code_challenge_method: 'S256',
    code_challenge: codeChallenge,
  });
  return `https://${cognitoDomain}/oauth2/authorize?${params.toString()}`;
}

export function getCognitoLogoutUrl(): string {
  const { cognitoDomain, cognitoClientId } = getConfig();
  const params = new URLSearchParams({
    client_id: cognitoClientId,
    logout_uri: getLogoutUri(),
  });
  return `https://${cognitoDomain}/logout?${params.toString()}`;
}

/**
 * Parse a JWT payload (no verification; the BFF verifies tokens server-side).
 */
export function parseJwtPayload(token: string): Record<string, unknown> {
  const base64Url = token.split('.')[1];
  if (!base64Url) throw new Error('Malformed JWT');
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const jsonPayload = decodeURIComponent(
    atob(padded)
      .split('')
      .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join(''),
  );
  return JSON.parse(jsonPayload);
}
